import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { ACTION_LINK_PATTERNS, getActionLabel } from 'src/shared/constants/action-link-patterns';

import { SharedLinkResponse } from 'src/models/shared-link.model';

@Pipe({
  name: 'enrichLinks',
  standalone: true,
  pure: true,
})
export class EnrichLinksPipe implements PipeTransform {

  private readonly sanitizer = inject(DomSanitizer);

  // Matches <a> tags produced by the linkify utility's known output format
  private readonly anchorTagRegex = /<a\s+href="([^"]*)"[^>]*>([^<]*)<\/a>/g;

  transform(html: string, enrichData?: Map<string, SharedLinkResponse>): SafeHtml {
    if (!html) {
      return '';
    }

    const result = html.replace(this.anchorTagRegex, (fullMatch, href) => {
      for (const actionPattern of ACTION_LINK_PATTERNS) {
        if (actionPattern.pattern.test(href)) {
          if (this.shouldRenderInline(href, actionPattern, enrichData)) {
            return '';
          }
          const inactive = this.isInactive(href, actionPattern, enrichData);
          const label = inactive
            ? this.resolveInactiveLabel(href, actionPattern, enrichData)
            : this.resolveLabel(href, actionPattern, enrichData);
          if (inactive) {
            return `<span class="action-link action-link-inactive">`
              + `<span class="action-link-icon">${actionPattern.iconSvg}</span>`
              + `<span class="action-link-label">${label}</span>`
              + `</span>`;
          }
          return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="action-link">`
            + `<span class="action-link-icon">${actionPattern.iconSvg}</span>`
            + `<span class="action-link-label">${label}</span>`
            + `</a>`;
        }
      }
      return fullMatch;
    });

    // Input is already XSS-safe (linkify escapes all raw HTML before creating <a> tags)
    return this.sanitizer.bypassSecurityTrustHtml(result);
  }

  private shouldRenderInline(
    href: string,
    pattern: typeof ACTION_LINK_PATTERNS[number],
    enrichData?: Map<string, SharedLinkResponse>
  ): boolean {
    if (pattern.key !== 'shared-link' || !pattern.uuidPattern) {
      return false;
    }
    const match = href.match(pattern.uuidPattern);
    if (!match) {
      return false;
    }
    if (!enrichData) {
      return true;
    }
    const data = enrichData.get(match[1]);
    if (!data) {
      return true;
    }
    if (data.shared_link_data?.expired) {
      return false;
    }
    return data.shared_link_data?.action_type === 'ai_task_add_payment_method';
  }

  private isInactive(
    href: string,
    pattern: typeof ACTION_LINK_PATTERNS[number],
    enrichData?: Map<string, SharedLinkResponse>
  ): boolean {
    if (!enrichData || !pattern.uuidPattern) {
      return false;
    }
    const match = href.match(pattern.uuidPattern);
    if (!match) {
      return false;
    }
    const data = enrichData.get(match[1]);
    return !!data?.shared_link_data?.expired;
  }

  private resolveInactiveLabel(
    href: string,
    pattern: typeof ACTION_LINK_PATTERNS[number],
    enrichData?: Map<string, SharedLinkResponse>
  ): string {
    const label = this.resolveLabel(href, pattern, enrichData);
    return `${label} (Expired)`;
  }

  private resolveLabel(
    href: string,
    pattern: typeof ACTION_LINK_PATTERNS[number],
    enrichData?: Map<string, SharedLinkResponse>
  ): string {
    if (!enrichData || !pattern.uuidPattern) {
      return pattern.label;
    }

    const match = href.match(pattern.uuidPattern);
    if (!match) {
      return pattern.label;
    }

    const uuid = match[1];
    const data = enrichData.get(uuid);
    if (!data?.shared_link_data?.action_type) {
      return pattern.label;
    }

    const actionLabel = getActionLabel(data.shared_link_data.action_type);
    const name = data.shared_link_data.customer?.first_name;
    return name ? `${actionLabel} for ${name}` : actionLabel;
  }
}
